import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { DeviceStatus } from '@/components/device/DeviceStatus'
import { Badge } from '@/components/ui/badge'
import { useDevices, useToggleFavorite, useRestoreDevice } from '@/hooks/useDevices'
import { Plus, Search, Star, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, BatteryLow, WifiOff, RotateCcw, Archive } from 'lucide-react'
import { format, formatDistanceToNow, differenceInHours, differenceInDays } from 'date-fns'
import type { DeviceFilters, Device } from '@/types/device.types'

type SortField = 'name' | 'serialNumber' | 'deviceType' | 'accountName' | 'status' | 'batteryLevel' | 'lastSeen'
type SortDirection = 'asc' | 'desc'
type QuickFilter = 'lowBattery' | 'missing24h' | 'missing7d'

export const DeviceListPage = () => {
  const [filters, setFilters] = useState<DeviceFilters>({
    activeOnly: true,
    search: '',
    page: 1,
    pageSize: 50
  })
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [quickFilter, setQuickFilter] = useState<QuickFilter | null>(null)
  const [showArchived, setShowArchived] = useState(false)

  const { data, isLoading, error } = useDevices(filters)
  const toggleFavorite = useToggleFavorite()
  const restoreDevice = useRestoreDevice()

  const handleToggleFavorite = (deviceId: string) => {
    toggleFavorite.mutate(deviceId)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const isLowBattery = (device: Device) =>
    device.batteryLevel != null && device.batteryLevel < 20

  const isMissing24h = (device: Device) =>
    device.lastSeen != null && differenceInHours(new Date(), new Date(device.lastSeen)) >= 24

  const isMissing7d = (device: Device) =>
    device.lastSeen != null && differenceInDays(new Date(), new Date(device.lastSeen)) >= 7

  const quickFilterCounts = useMemo(() => {
    if (!data?.devices) return { lowBattery: 0, missing24h: 0, missing7d: 0 }
    return {
      lowBattery: data.devices.filter(isLowBattery).length,
      missing24h: data.devices.filter(isMissing24h).length,
      missing7d: data.devices.filter(isMissing7d).length,
    }
  }, [data?.devices])

  const handleQuickFilter = (filter: QuickFilter) => {
    setQuickFilter(quickFilter === filter ? null : filter)
  }

  const sortedDevices = useMemo(() => {
    if (!data?.devices) return []

    // Apply quick filter
    let devices = data.devices
    if (quickFilter === 'lowBattery') {
      devices = devices.filter(isLowBattery)
    } else if (quickFilter === 'missing24h') {
      devices = devices.filter(isMissing24h)
    } else if (quickFilter === 'missing7d') {
      devices = devices.filter(isMissing7d)
    }

    const sorted = [...devices].sort((a, b) => {
      let aVal: any = a[sortField]
      let bVal: any = b[sortField]

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1

      // Special handling for lastSeen dates
      if (sortField === 'lastSeen') {
        aVal = aVal ? new Date(aVal).getTime() : 0
        bVal = bVal ? new Date(bVal).getTime() : 0
      }

      // String comparison (case insensitive)
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [data?.devices, sortField, sortDirection, quickFilter])

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    )
  }

  if (error) {
    return <ErrorMessage message="Failed to load devices. Please try again." />
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold">Devices</h1>
        <Button asChild className="w-full sm:w-auto">
          <Link to="/devices/new">
            <Plus className="mr-2 h-4 w-4" />
            Add Device
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-col sm:flex-row flex-wrap gap-4 pt-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search devices..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9"
            />
          </div>
          <Select
            value={filters.deviceType || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, deviceType: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Device Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="watersense">Water Sense</SelectItem>
              <SelectItem value="rk900">RK900</SelectItem>
              <SelectItem value="dht11soil">DHT11 Soil</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, status: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="ACTIVATED">Activated</SelectItem>
              <SelectItem value="INACTIVE">Inactive</SelectItem>
              <SelectItem value="DECOMMISSIONED">Decommissioned</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.pageSize.toString()}
            onValueChange={(value) =>
              setFilters({ ...filters, pageSize: parseInt(value), page: 1 })
            }
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Per Page" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25 per page</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
              <SelectItem value="200">200 per page</SelectItem>
              <SelectItem value="500">500 per page</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={filters.favoritesOnly ? 'default' : 'outline'}
            onClick={() =>
              setFilters({ ...filters, favoritesOnly: !filters.favoritesOnly, page: 1 })
            }
          >
            <Star
              className={`mr-2 h-4 w-4 ${filters.favoritesOnly ? 'fill-current' : ''}`}
            />
            Favorites Only
          </Button>
          <Button
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => {
              setShowArchived(!showArchived)
              if (!showArchived) {
                setFilters({ ...filters, activeOnly: false, page: 1 })
              } else {
                setFilters({ ...filters, activeOnly: true, page: 1 })
              }
            }}
          >
            <Archive className={`mr-2 h-4 w-4`} />
            Show Archived
          </Button>
        </CardContent>
      </Card>

      {/* Quick Filters */}
      {data?.devices && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={quickFilter === 'lowBattery' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleQuickFilter('lowBattery')}
          >
            <BatteryLow className="mr-2 h-4 w-4" />
            Low Battery ({quickFilterCounts.lowBattery})
          </Button>
          <Button
            variant={quickFilter === 'missing24h' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleQuickFilter('missing24h')}
          >
            <WifiOff className="mr-2 h-4 w-4" />
            Missing 24h ({quickFilterCounts.missing24h})
          </Button>
          <Button
            variant={quickFilter === 'missing7d' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleQuickFilter('missing7d')}
          >
            <WifiOff className="mr-2 h-4 w-4" />
            Missing 7d ({quickFilterCounts.missing7d})
          </Button>
        </div>
      )}

      {/* Device Table */}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('name')}
                    >
                      Name
                      <SortIcon field="name" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('serialNumber')}
                    >
                      Serial Number
                      <SortIcon field="serialNumber" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('deviceType')}
                    >
                      Type
                      <SortIcon field="deviceType" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('accountName')}
                    >
                      Account
                      <SortIcon field="accountName" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('status')}
                    >
                      Status
                      <SortIcon field="status" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('batteryLevel')}
                    >
                      Battery
                      <SortIcon field="batteryLevel" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('lastSeen')}
                    >
                      Last Seen
                      <SortIcon field="lastSeen" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedDevices && sortedDevices.length > 0 ? (
                  sortedDevices.map((device) => (
                    <TableRow key={device.id}>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleFavorite(device.id)}
                        >
                          <Star
                            className={`h-4 w-4 ${
                              device.isFavorite ? 'fill-yellow-400 text-yellow-400' : ''
                            }`}
                          />
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">
                        <Link to={`/devices/${device.id}`} className="hover:underline">
                          {device.name}
                        </Link>
                      </TableCell>
                      <TableCell>{device.serialNumber}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{device.deviceType}</Badge>
                      </TableCell>
                      <TableCell>{device.accountName}</TableCell>
                      <TableCell>
                        <DeviceStatus status={device.status} />
                      </TableCell>
                      <TableCell>
                        {device.batteryLevel ? `${device.batteryLevel}%` : '-'}
                      </TableCell>
                      <TableCell>
                        {device.lastSeen ? (
                          <span title={format(new Date(device.lastSeen), 'MMM d, yyyy HH:mm:ss')}>
                            {formatDistanceToNow(new Date(device.lastSeen), { addSuffix: true })}
                          </span>
                        ) : (
                          'Never'
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {device.status === 'DECOMMISSIONED' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => restoreDevice.mutate(device.id)}
                              disabled={restoreDevice.isPending}
                            >
                              <RotateCcw className="mr-1 h-3 w-3" />
                              Restore
                            </Button>
                          )}
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/devices/${device.id}/edit`}>Edit</Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No devices found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {sortedDevices && sortedDevices.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((filters.page - 1) * filters.pageSize) + 1} to {Math.min(filters.page * filters.pageSize, data?.total || 0)} of {data?.total || 0} devices
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              disabled={filters.page === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {filters.page} of {Math.ceil((data?.total || 0) / filters.pageSize)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={filters.page >= Math.ceil((data?.total || 0) / filters.pageSize)}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
